/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext } from 'react';
import '../../../scss/TooltipCommonTheme.scss';
import { Chart } from '@scuf/charts';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import { ITrendChartProps } from './ITrendChartProps';
import './TrendChart.scss';
import {
  formatCurrency,
  getSymbolFromCurrency,
  getValueFromArray,
  baselineCostDataDisplay,
  actualCostDataDisplay,
  previousAvoidanceDataDisplay,
  actualAvoidanceDataDisplay,
  partialDateCheck,
  partialDateCheckUtilitycost
} from '../Utils/Utils';
import { MAGIC_VALUE } from '../Utils/Constants';
import { getTrendChartLabel } from '../Utils/Graph/AllGraphUtils';
import {
  baselineConsumptionTooltipPaneItem,
  actualConsumptionTooltipPaneItem,
  actualAvoidanceTooltipPaneItem,
  previousAvoidanceTooltipPaneItem
} from './TrendChartUtils';
import { UserContext } from '../../DetailKPIWidgets/CarbonSiteViewDetailsComponent/TotalEmissionDetailWidget/TotalEmissionDetailWidget';
import {
  EnergyUsageDataSourceType,
  EnergyUsageField,
  SmartPowerSubscriptionType
} from '../Enums';
import {
  IAny,
  IRequestContext
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { IMessageDescriptor } from '@scuf/localization/dist/types';

interface ITooltipDetails {
  label: string;
  x: number;
  y: number;
  high?: number;
  low?: number;
  pointValue: number;
  xCategory?: string;
  yCategory?: string;
  colorIndex: number;
}

const evaluateConditions = (
  baselineCostData: IAny,
  actualCostData: IAny,
  actualAvoidanceCost: IAny,
  previousAvoidanceCost: IAny
) => {
  const isBaselineCostData =
    baselineCostData !== undefined && baselineCostData !== '';
  const isActualCostData =
    actualCostData !== undefined && actualCostData !== '';
  const isActualAvoidanceCost =
    actualAvoidanceCost !== undefined && actualAvoidanceCost !== '';
  const isPreviousAvoidanceCost =
    previousAvoidanceCost !== undefined && previousAvoidanceCost !== '';
  return [
    isBaselineCostData,
    isActualCostData,
    isActualAvoidanceCost,
    isPreviousAvoidanceCost
  ].some((cost: IAny) => cost);
};

const isPremiumSubscriptionCheck = (
  isCarbonAvoidance: boolean,
  requestContext: IRequestContext | undefined
) =>
  isCarbonAvoidance
    ? true
    : requestContext?.context?.smpSubscriptionType ===
      SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION
    ? true
    : false;

const actualConsumptionTooltipPaneItemCheck = (
  isCarbonAvoidance: boolean | undefined,
  actualConsumptionData: (number | null)[] | undefined,
  category: string | undefined,
  t: {
    (
      key: string | IMessageDescriptor,
      option?: Record<string, any> | undefined
    ): string;
    (
      key: string | IMessageDescriptor,
      option?: Record<string, any> | undefined
    ): string;
  },
  isCompletedData: boolean,
  partialDate: number,
  actualValue: number | null | undefined,
  measurementUnit: string,
  renderedFromCarbon?: boolean
) =>
  !isCarbonAvoidance &&
  actualConsumptionTooltipPaneItem(
    actualConsumptionData,
    category,
    t,
    isCompletedData,
    partialDate,
    actualValue,
    measurementUnit,
    renderedFromCarbon
  );

const baselineConsumptionTooltipPaneItemCheck = (
  isCarbonAvoidance: boolean | undefined,
  baselineConsumptionData: (number | null)[] | undefined,
  category: string | undefined,
  t: {
    (
      key: string | IMessageDescriptor,
      option?: Record<string, any> | undefined
    ): string;
    (
      key: string | IMessageDescriptor,
      option?: Record<string, any> | undefined
    ): string;
  },
  baselineValue: number | null | undefined,
  measurementUnit: string,
  renderedFromCarbon?: boolean
) =>
  !isCarbonAvoidance &&
  baselineConsumptionTooltipPaneItem(
    baselineConsumptionData,
    category,
    t,
    baselineValue,
    measurementUnit,
    renderedFromCarbon
  );

const actualAvoidanceTooltipPaneItemCheck = (
  isPremiumSubscription: boolean,
  actualAvoidanceData: (number | null)[] | undefined,
  category: string | undefined,
  t: {
    (
      key: string | IMessageDescriptor,
      option?: Record<string, any> | undefined
    ): string;
    (
      key: string | IMessageDescriptor,
      option?: Record<string, any> | undefined
    ): string;
  },
  actualAvoidance: number | null | undefined,
  measurementUnit: string,
  renderedFromCarbon?: boolean
) =>
  isPremiumSubscription &&
  actualAvoidanceTooltipPaneItem(
    actualAvoidanceData,
    category,
    t,
    actualAvoidance,
    measurementUnit,
    renderedFromCarbon
  );

const previousAvoidanceTooltipPaneItemCheck = (
  isPremiumSubscription: boolean,
  previousAvoidanceData: (number | null)[] | undefined,
  category: string | undefined,
  t: {
    (
      key: string | IMessageDescriptor,
      option?: Record<string, any> | undefined
    ): string;
    (
      key: string | IMessageDescriptor,
      option?: Record<string, any> | undefined
    ): string;
  },
  baselineAvoidance: number | null | undefined,
  measurementUnit: string,
  renderedFromCarbon?: boolean
) =>
  isPremiumSubscription &&
  previousAvoidanceTooltipPaneItem(
    previousAvoidanceData,
    category,
    t,
    baselineAvoidance,
    measurementUnit,
    renderedFromCarbon
  );

const actualCostDataCurrency = (
  isCarbonAvoidance: boolean | undefined,
  actualCostData: number[] | undefined,
  costCurrency: string | undefined,
  actualCost: number | null | undefined,
  t: IAny,
  d: IAny,
  isCompletedData: IAny,
  partialDate: IAny
) => {
  return !isCarbonAvoidance && actualCostData ? (
    <div className="tooltip-pane-item">
      <div className="tooltip-heading-container">
        <span className="dot" style={{ backgroundColor: '#1272B2' }}></span>
        <span className="tooltip-header-label emaasThemeColor">
          {/* {getSymbolFromCurrency(costCurrency!)}  */}
          {t(d.ACTUAL)}
          {partialDateCheckUtilitycost(isCompletedData, partialDate, t)}
        </span>
      </div>
      <div>
        <span className="tooltip-header-value emaasThemeColor">
          {formatCurrency(actualCost!, costCurrency, false)}
        </span>
      </div>
    </div>
  ) : (
    undefined
  );
};

const baselineCostDataCurrency = (
  isCarbonAvoidance: boolean | undefined,
  costCurrency: string | undefined,
  category: string | undefined,
  baselineCost: number | null | undefined,
  t: IAny,
  d: IAny,
  baselineCostData: number[] | undefined,
  renderedFromCarbon?: boolean
) => {
  return !isCarbonAvoidance && baselineCostData ? (
    <div className="tooltip-pane-item">
      <div className="tooltip-heading-container">
        <span className="dot" style={{ backgroundColor: '#606061' }}></span>
        <span className="tooltip-header-label emaasThemeColor">
          {/* {getSymbolFromCurrency(costCurrency!)}{' '} */}
          {renderedFromCarbon
            ? t(d.PREVIOUSEMISSIONS)
            : getTrendChartLabel(category, t(d.BASELINE), t(d.PREVIOUSPERIOD))}
        </span>
      </div>
      <div>
        <span className="tooltip-header-value emaasThemeColor">
          {formatCurrency(baselineCost!, costCurrency, false)}
        </span>
      </div>
    </div>
  ) : (
    undefined
  );
};

const actualAvoidanceCostCurrency = (
  isPremiumSubscription: boolean,
  actualAvoidanceCost: number[] | undefined,
  costCurrency: string | undefined,
  avoidanceCost: number | null | undefined,
  t: IAny,
  d: IAny
) => {
  return isPremiumSubscription && actualAvoidanceCost ? (
    <div className="tooltip-pane-item">
      <div className="tooltip-heading-container">
        <span className="dot" style={{ backgroundColor: '#1272B2' }}></span>
        <span className="tooltip-header-label emaasThemeColor">
          {getSymbolFromCurrency(costCurrency!)} {t(d.ACTUALAVOIDANCE)}
        </span>
      </div>
      <div>
        <span className="tooltip-header-value emaasThemeColor">
          {formatCurrency(avoidanceCost!, costCurrency, false)}
        </span>
      </div>
    </div>
  ) : (
    undefined
  );
};

const previousAvoidanceCostCurrency = (
  isPremiumSubscription: boolean,
  previousAvoidanceCost: number[] | undefined,
  costCurrency: string | undefined,
  category: string | undefined,
  previousCost: number | null | undefined,
  t: IAny,
  d: IAny
) => {
  return isPremiumSubscription && previousAvoidanceCost ? (
    <div className="tooltip-pane-item">
      <div className="tooltip-heading-container">
        <span className="dot" style={{ backgroundColor: '#606061' }}></span>
        <span className="tooltip-header-label emaasThemeColor">
          {getSymbolFromCurrency(costCurrency!)}{' '}
          {getTrendChartLabel(category, t(d.BASELINE), t(d.PREVIOUSAVOIDANCE))}
        </span>
      </div>
      <div>
        <span className="tooltip-header-value emaasThemeColor">
          {formatCurrency(previousCost!, costCurrency, false)}
        </span>
      </div>
    </div>
  ) : (
    undefined
  );
};

const baselineCostDataDisplayCheck = (
  isCarbonAvoidance: boolean | undefined,
  baselineCostData: number[] | undefined,
  measurementUnit: string,
  baselineConsumptionData: (number | null)[] | undefined,
  t: IAny
) =>
  !isCarbonAvoidance &&
  baselineCostDataDisplay(
    baselineCostData,
    measurementUnit,
    baselineConsumptionData,
    t
  );
const TrendChart = (props: ITrendChartProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  let isPremiumSubscription: boolean = false;
  const {
    costCurrency,
    measurementUnit,
    chartData,
    legendItems,
    category,
    isCarbonAvoidance,
    isSite
  } = props;
  let {
    baselineConsumptionData,
    baselineCostData,
    actualConsumptionData,
    actualCostData,
    actualAvoidanceData,
    previousAvoidanceData,
    actualAvoidanceCost,
    previousAvoidanceCost,
    IsCompleted,
    YearIndex,
    MonthIndex,
    PeriodUpto,
    SiteWithDataCount,
    totalSiteCount
  } = chartData;
  if (isCarbonAvoidance !== undefined) {
    const requestContext = useContext(UserContext);
    isPremiumSubscription = isPremiumSubscriptionCheck(
      isCarbonAvoidance,
      requestContext
    );
  }

  const renderTooltip = (details: ITooltipDetails) => {
    {
      const { x } = details;
      const period = PeriodUpto[x];
      const tooltipMonth = MonthIndex[x];
      const tooltipYear = YearIndex[x];
      const siteWithDataCount: any =
        SiteWithDataCount && typeof SiteWithDataCount === 'object'
          ? SiteWithDataCount[x]
          : SiteWithDataCount ?? null;
      const actualValue = getValueFromArray(actualConsumptionData, x);
      const baselineValue = getValueFromArray(baselineConsumptionData, x);
      const actualCost = getValueFromArray(actualCostData, x);
      const baselineCost = getValueFromArray(baselineCostData, x);
      const actualAvoidance = getValueFromArray(actualAvoidanceData, x);
      const baselineAvoidance = getValueFromArray(previousAvoidanceData, x);
      const avoidanceCost = getValueFromArray(actualAvoidanceCost, x);
      const previousCost = getValueFromArray(previousAvoidanceCost, x);
      const partialDate = period;

      const isCompletedData = IsCompleted[x];
      if (
        (details.label.includes('Actual') &&
          !details.label.includes('Avoidance')) ||
        (isCarbonAvoidance && !details.label.includes('Actual'))
      )
        return (
          <div className="emaas-chart-widget-container">
            <div className="tooltip-top">
              <div>
                <span className="tooltip-date emaasThemeColor">
                  {tooltipMonth} {tooltipYear}
                </span>
              </div>
              <div className="tooltip-pane">
                {actualConsumptionTooltipPaneItemCheck(
                  isCarbonAvoidance,
                  actualConsumptionData,
                  category,
                  t,
                  isCompletedData,
                  partialDate,
                  actualValue,
                  measurementUnit,
                  props.renderedFromCarbon
                )}
                {baselineConsumptionTooltipPaneItemCheck(
                  isCarbonAvoidance,
                  baselineConsumptionData,
                  category,
                  t,
                  baselineValue,
                  measurementUnit,
                  props.renderedFromCarbon
                )}
                {actualAvoidanceTooltipPaneItemCheck(
                  isPremiumSubscription,
                  actualAvoidanceData,
                  category,
                  t,
                  actualAvoidance,
                  measurementUnit,
                  props.renderedFromCarbon
                )}
                {previousAvoidanceTooltipPaneItemCheck(
                  isPremiumSubscription,
                  previousAvoidanceData,
                  category,
                  t,
                  baselineAvoidance,
                  measurementUnit,
                  props.renderedFromCarbon
                )}
                {actualCostDataCurrency(
                  isCarbonAvoidance,
                  actualCostData,
                  costCurrency,
                  actualCost,
                  t,
                  d,
                  isCompletedData,
                  partialDate
                )}
                {baselineCostDataCurrency(
                  isCarbonAvoidance,
                  costCurrency,
                  category,
                  baselineCost,
                  t,
                  d,
                  baselineCostData,
                  props.renderedFromCarbon
                )}
                {actualAvoidanceCostCurrency(
                  isPremiumSubscription,
                  actualAvoidanceCost,
                  costCurrency,
                  avoidanceCost,
                  t,
                  d
                )}
                {previousAvoidanceCostCurrency(
                  isPremiumSubscription,
                  previousAvoidanceCost,
                  costCurrency,
                  category,
                  previousCost,
                  t,
                  d
                )}
              </div>
              {siteWithDataCount !== null &&
                siteWithDataCount !== undefined &&
                totalSiteCount &&
                siteWithDataCount !== 0 &&
                siteWithDataCount < totalSiteCount &&
                (category === EnergyUsageField.ENERGY_USAGE ||
                  category === 'all' ||
                  category === 'CO2') &&
                !props.renderedFromCarbon &&
                props.showAllTooltips && (
                  <div className="tooltip-pane">
                    <p className="basic-site-with-data-count-msg">
                      {t(d.BASICSITEWITHDATACOUNTMESSAGE, {
                        siteWithDataCount,
                        totalSiteCount
                      })}
                    </p>
                  </div>
                )}
              {props.renderedFromCarbon ? (
                <div className="tooltip-pane-item">
                  <p className="basic-site-with-data-count-msg">
                    {props.dataSourceType ===
                    EnergyUsageDataSourceType.UTILITY_METER
                      ? t(d.CARBONUTILITYGRAPHTOOLTIP, {
                          siteWithDataCount,
                          totalSiteCount
                        })
                      : ''
                    // : t(d.CARBONLIVEGRAPHTOOLTIP,
                    //   {
                    //     siteWithDataCount,
                    //     totalSiteCount
                    //   }
                    //   )
                    }
                  </p>
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="trend-chart-widget">
      <Chart
        className="trend-chart"
        colors={legendItems.map((item) => item.color)}
        spacing={[
          MAGIC_VALUE.THIRTY,
          MAGIC_VALUE.ZERO,
          MAGIC_VALUE.ZERO,
          MAGIC_VALUE.ZERO
        ]}
        showLegend={false}
      >
        {baselineCostDataDisplayCheck(
          isCarbonAvoidance,
          baselineCostData,
          measurementUnit,
          baselineConsumptionData,
          t
        )}
        {!isCarbonAvoidance &&
          actualCostDataDisplay(
            actualCostData,
            measurementUnit,
            actualConsumptionData,
            t
          )}
        {isPremiumSubscription &&
          previousAvoidanceDataDisplay(
            previousAvoidanceCost,
            measurementUnit,
            previousAvoidanceData,
            t
          )}
        {isPremiumSubscription &&
          actualAvoidanceDataDisplay(
            actualAvoidanceCost,
            measurementUnit,
            actualAvoidanceData,
            t
          )}
        <Chart.XAxis
          lineColor="#d0d0d0"
          className="month-labels"
          allowDecimals={false}
          showGridLines={false}
          labels={{ autoRotation: false }}
          categories={MonthIndex}
        />
        {evaluateConditions(
          baselineCostData,
          actualCostData,
          actualAvoidanceCost,
          previousAvoidanceCost
        ) ? (
          <Chart.YAxis
            gridLineColor="#d0d0d0"
            gridLineWidth={0.5}
            showGridLines={true}
            visible={true}
            opposite={false}
            labels={{
              formatter: function() {
                return formatCurrency(this.value, costCurrency, false);
              }
            }}
            className="trend-chart-yaxis"
            tickAmount={4}
          />
        ) : (
          <Chart.YAxis
            gridLineColor="#d0d0d0"
            gridLineWidth={0.5}
            showGridLines={true}
            visible={true}
            opposite={false}
            labelSuffix={`${''} ${measurementUnit}`}
            className="trend-chart-yaxis"
            tickAmount={4}
          />
        )}
        <Chart.Tooltip
          borderRadius={10}
          valueSuffix={measurementUnit}
          renderContent={renderTooltip}
          backgroundColor="white"
          shared={true}
        />
      </Chart>
      <div className="trend-chart-legends-container">
        <div className="legends-flex-container">
          {legendItems.map((item) => {
            if (item.type == 'Bar') {
              return (
                <div
                  className={`bar-chart-legend-container ${item.className}`}
                  style={{
                    borderLeft: `0.15rem solid ${item.color}`,
                    height: '1.25rem',
                    width: 'auto',
                    alignItems: 'center',
                    lineHeight: '1.25rem'
                  }}
                  key={item.className}
                >
                  <span className="Bar-chart-legend-text">{item.text}</span>
                </div>
              );
            }
            if (item.type == 'Line') {
              return (
                <div
                  className={`line-chart-legend-container ${item.className}`}
                >
                  <div
                    className="line-chart-legend-icon"
                    style={{ backgroundColor: item.color }}
                  >
                    <span
                      className="dot"
                      Bar-chart-legend-text
                      style={{ backgroundColor: item.color }}
                    ></span>
                  </div>
                  <span className="line-chart-legend-text">{item.text}</span>
                </div>
              );
            }
          })}
        </div>
      </div>
    </div>
  );
};

export default TrendChart;
